import { QueryClient } from '@tanstack/react-query';

import { useGetKeywordQuestionCardCursorPageQuery } from '@/hooks/qna/mobile/qna.gql.impl';

import {
  AnswerLikeType,
  ProfessionalFieldType,
  QuestionAccessibleType,
  QuestionAnsweredStatus,
} from '@/types/bff/gql-schema';
import { AnswererType } from '@/types/bff/gql-schema';
import { GetKeywordQuestionCardCursorPageQuery } from '@/types/bff/gql-schema';
import {
  EAnswerLikeType,
  EProfessionalFieldType,
  EQuestionAccessibleType,
  EQuestionAnsweredStatus,
} from '@/types/search';
import { EAnswererType } from '@/types/search';
import { KeywordSearchQuestionVo } from '@/types/search';
import { displayedAt, getQnaAnswerStatus } from '@/utils/format';
import useGetKeywordQuestionCardInfiniteQuery from './useGetKeywordQuestionCardInfiniteQuery';

interface Props {
  keyword: string;
  size?: number;
  enabled?: boolean;
}

const QUESTION_SIZE = 20;
const answererTypeEnumParser = (answererType: AnswererType | null | undefined): EAnswererType | undefined => {
  switch (answererType) {
    case AnswererType.Ai:
      return EAnswererType.Ai;
    case AnswererType.Doctor:
      return EAnswererType.Doctor;
    case AnswererType.Nurse:
      return EAnswererType.Nurse;
  }
};

const myLikeTypeEnumParser = (myLikeType: AnswerLikeType | null | undefined): EAnswerLikeType | undefined => {
  if (myLikeType === AnswerLikeType.Like) {
    return EAnswerLikeType.Like;
  }
  if (myLikeType === AnswerLikeType.Dislike) {
    return EAnswerLikeType.Dislike;
  }
  return undefined;
};

const professionalFieldTypeEnumParser = (professionalFieldType: ProfessionalFieldType): EProfessionalFieldType => {
  switch (professionalFieldType) {
    case ProfessionalFieldType.Department:
      return EProfessionalFieldType.Department;
  }
};

const questionAccessibleTypeEnumParser = (questionAccessibleType: QuestionAccessibleType): EQuestionAccessibleType => {
  switch (questionAccessibleType) {
    case QuestionAccessibleType.All:
      return EQuestionAccessibleType.All;
    case QuestionAccessibleType.DoctorsAndMe:
      return EQuestionAccessibleType.DoctorsAndMe;
    case QuestionAccessibleType.Me:
      return EQuestionAccessibleType.Me;
  }
};

const questionAnsweredStatusEnumParser = (questionAnsweredStatus: QuestionAnsweredStatus): EQuestionAnsweredStatus => {
  switch (questionAnsweredStatus) {
    case QuestionAnsweredStatus.Answered:
      return EQuestionAnsweredStatus.Answered;
    case QuestionAnsweredStatus.Answering:
      return EQuestionAnsweredStatus.Answering;
    case QuestionAnsweredStatus.Checking:
      return EQuestionAnsweredStatus.Checking;
  }
};

const parseSearchResultQuestionVo = (
  data: Array<GetKeywordQuestionCardCursorPageQuery>
): Array<KeywordSearchQuestionVo> => {
  if (data) {
    return data
      .flatMap((page) => page.keywordSearchQuestionCardCursorPage.elements)
      .map((element) => {
        return {
          ...element,
          answeredStatus: questionAnsweredStatusEnumParser(element.answeredStatus),
          question: {
            ...element.question,
            accessibleType: questionAccessibleTypeEnumParser(element.question.accessibleType),
          },
          answer: element.answer
            ? {
                ...element.answer,
                myLikeType: myLikeTypeEnumParser(element.answer?.myLikeType),
                answerer: {
                  ...element.answer.answerer,
                  answererType: answererTypeEnumParser(element.answer.answerer?.answererType),

                  professionalFields: element.answer.answerer?.professionalFields.map((professionalField) => ({
                    ...professionalField,
                    professionalFieldType: professionalFieldTypeEnumParser(professionalField.professionalFieldType),
                  })),
                },
              }
            : undefined,
          questionCreatedAt: displayedAt(element.question.createdAt),
          answerCreatedAt: element.answer ? displayedAt(element.answer.createdAt) : null,
          answeredStatusText: getQnaAnswerStatus(element.answeredStatus),
        };
      });
  }
  return [] as Array<KeywordSearchQuestionVo>;
};

const useGetKeywordQuestionCardInfinite = ({ keyword, size = QUESTION_SIZE, enabled = true }: Props) => {
  const {
    data: searchedQnaData,
    isLoading: isSearchedQnaLoading,
    hasNextPage: hasSearchedQnaNextPage,
    fetchNextPage: fetchSearchedQnaNextPage,
  } = useGetKeywordQuestionCardInfiniteQuery(
    {
      request: {
        keyword,
        size,
      },
    },
    {
      select: (data) => {
        return {
          pages: parseSearchResultQuestionVo(data.pages),
          pageParams: data.pageParams,
        };
      },
      enabled: enabled && !!keyword && keyword.length > 0,
    }
  );

  const qnas = searchedQnaData?.pages.flatMap((qna) => qna) || [];

  return {
    qnas,
    searchedQnaData,
    isSearchedQnaLoading,
    hasSearchedQnaNextPage,
    fetchSearchedQnaNextPage,
  };
};

export default useGetKeywordQuestionCardInfinite;

const getNonBenefitHomeQnaParams = (keyword: string) => {
  return {
    keyword,
    size: 5,
  };
};

useGetKeywordQuestionCardInfinite.getNonBenefitHomeQnaParams = getNonBenefitHomeQnaParams;

const prefetchQuery = async (queryClient: QueryClient, params: Pick<Props, 'keyword'>) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordQuestionCardCursorPageQuery.getKey({
      request: {
        ...getNonBenefitHomeQnaParams(params.keyword),
      },
    }),
    useGetKeywordQuestionCardCursorPageQuery.fetcher({
      request: {
        ...getNonBenefitHomeQnaParams(params.keyword),
      },
    })
  );
};

useGetKeywordQuestionCardInfinite.prefetchQuery = prefetchQuery;
